import { useCallback, useState } from 'react';
import {
  ContainerEditModal,
  ContainerListModal,
  ContainerSellModal,
  ContainerTransferModal,
  ContainerUpdateHoldModal,
  fullScreenButtonClick,
  ImageGallery,
  RepairSetUpdateMultipleModal,
  ShareImagesModal,
  UploadImagesModal,
  enqueueSnackbar,
} from '../../design-system';
import { ContainersPage, ContainersHeader, ContainersBody } from './ContainersComponents';
import { ContainerGetOneQuery } from '../../types';
import { containersService } from '../../services';
import { i18n } from '../../i18n';
import { GateInReceiptPrint, GateOutReceiptPrint } from './GateInReceiptPrint';

type GateReceipt = {
  type: 'gate-in' | 'gate-out';
  container: ContainerGetOneQuery;
};

export const Containers = () => {
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [checkIndexs, setCheckIndexs] = useState<number[]>([]);
  const [images, setImages] = useState<any>([]);
  const [containerList, setContainerList] = useState<any>();
  const [exportHandler, setExportHandler] = useState<(() => void) | null>(null);
  const [gateReceipt, setGateReceipt] = useState<GateReceipt | null>(null);

  const handlePrintGateReceipt = useCallback((id: string, type: GateReceipt['type']) => {
    containersService
      .get(id)
      .then((res) => setGateReceipt({ type, container: res.data.data }))
      .catch(() =>
        enqueueSnackbar(
          type === 'gate-in'
            ? i18n.t('pages.gateInReceipt.loadError')
            : i18n.t('pages.gateOutReceipt.loadError'),
          'error',
        ),
      );
  }, []);

  const handleReceiptPrinted = useCallback(() => setGateReceipt(null), []);

  const handleCheckboxChange = (i: number) => {
    if (isNaN(i)) {
      const allCheckIndexs = Array.from({ length: data.length }, (_, i) => i);
      setCheckIndexs(checkIndexs.length === data.length ? [] : allCheckIndexs);
      return;
    }

    setCheckIndexs((prev) =>
      prev.includes(i) ? prev.filter((index) => index !== i) : [...prev, i],
    );
  };

  const handleClickImage = (images: any[]) => {
    setImages(images.map(({ url }) => url));
    fullScreenButtonClick('containers');
  };

  return (
    <>
      <ContainersPage innerScroll>
        <ContainersHeader
          totalCount={totalCount}
          containerList={containerList}
          setContainerList={setContainerList}
          handleExportExcelClick={() => exportHandler?.()}
        />
        <ContainersBody
          setData={setData}
          setTotalCount={setTotalCount}
          containerList={containerList}
          setContainerList={setContainerList}
          checkIndexs={checkIndexs}
          setCheckIndexs={setCheckIndexs}
          handleCheckboxChange={handleCheckboxChange}
          handleClickImage={handleClickImage}
          setExportHandler={setExportHandler}
          onPrintGateInReceipt={(id: string) => handlePrintGateReceipt(id, 'gate-in')}
          onPrintGateOutReceipt={(id: string) => handlePrintGateReceipt(id, 'gate-out')}
        />
      </ContainersPage>
      <ContainerListModal validContainers />
      <ContainerEditModal />
      <ContainerTransferModal />
      <ContainerSellModal />
      <ContainerUpdateHoldModal />
      <RepairSetUpdateMultipleModal />
      <UploadImagesModal />
      <ShareImagesModal />
      <ImageGallery id={'containers'} photos={images} startIndex={0} />
      {gateReceipt?.type === 'gate-in' && (
        <GateInReceiptPrint container={gateReceipt.container} onPrinted={handleReceiptPrinted} />
      )}
      {gateReceipt?.type === 'gate-out' && (
        <GateOutReceiptPrint container={gateReceipt.container} onPrinted={handleReceiptPrinted} />
      )}
    </>
  );
};
